/**
 * Global fetch interceptor to inject authentication token into all requests
 * This ensures ChatKit SDK and other API calls include the Authorization header
 */

import { getToken, removeToken } from "./auth";

// Store the original fetch function
const originalFetch = globalThis.fetch;

// Callback for 401 errors (token expired or invalid)
let onUnauthorized: (() => void) | null = null;

/**
 * Set callback to be called when a 401 response is received
 */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/**
 * Check if a URL should have the token injected
 */
function shouldInjectToken(url: RequestInfo | URL): boolean {
  const urlString =
    typeof url === "string"
      ? url
      : url instanceof URL
      ? url.toString()
      : url instanceof Request
      ? url.url
      : "";

  // Never inject token for login endpoint
  if (urlString.includes("/login")) {
    return false;
  }

  // Handle absolute URLs (http:// or https://)
  if (urlString.startsWith("http://") || urlString.startsWith("https://")) {
    // Check if it's our backend URL
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL ?? "/api";

    if (backendUrl.startsWith("http")) {
      // Full backend URL provided
      try {
        const backendOrigin = new URL(backendUrl).origin;
        const requestOrigin = new URL(urlString).origin;
        // Inject if it's the same origin as our backend
        return requestOrigin === backendOrigin;
      } catch {
        return false;
      }
    }

    // If backend URL is relative but request is absolute, don't inject
    // (might be external API)
    return false;
  }

  // Relative URLs - inject token for API routes (except login which we already checked)
  return urlString.startsWith("/api");
}

/**
 * Initialize the fetch interceptor
 * This should be called once when the app loads
 */
export function setupFetchInterceptor(): void {
  if (typeof window === "undefined") {
    // Server-side: don't intercept
    return;
  }

  // Override global fetch
  globalThis.fetch = async function (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    // Get the token
    const token = getToken();

    // Clone init to avoid mutating the original
    const modifiedInit: RequestInit = { ...init };

    // Only inject token if we have one and the URL matches our criteria
    if (token && shouldInjectToken(input)) {
      // Ensure headers object exists
      const headers = new Headers(modifiedInit.headers);

      // Only add Authorization header if it's not already present
      if (!headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      modifiedInit.headers = headers;
    }

    // Call the original fetch with modified init
    const response = await originalFetch(input, modifiedInit);

    // Handle 401 Unauthorized responses (token expired or invalid)
    if (response.status === 401 && shouldInjectToken(input)) {
      // Remove invalid token
      removeToken();
      // Notify handler if set
      if (onUnauthorized) {
        onUnauthorized();
      }
    }

    return response;
  };
}

/**
 * Restore the original fetch function (useful for testing)
 */
export function restoreFetch(): void {
  globalThis.fetch = originalFetch;
}
